import { FlexiSlider } from "appworks/graphics/elements/flexi-slider";
import { FlexiText } from "appworks/graphics/elements/flexi-text";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { uiFlags } from "appworks/ui/flags/ui-flags";
import { ValueList } from "appworks/utils/value-list";
import { slotModel } from "slotworks/model/slot-model";
import { AgsAbstractSliderControl } from "./ags-abstract-slider-control";

/**
 * A slider control that manages the bet credit sizes for the slot game.
 * Extends the `AgsAbstractSliderControl` abstract class.
 */
export class AgsBetSliderControl extends AgsAbstractSliderControl {
    /**
     * Sets the enabled state of the object.
     * The object's enabled state depends on the value of the 'enabled' parameter and the availability of free bets.
     *
     * @param enabled - A boolean value indicating whether the object should be enabled.
     *                  'true' to enable the object, 'false' to disable it.
     */
    public setEnabled(enabled: boolean): void {
        const { freeBet } = slotModel.read().bet;
        super.setEnabled(!freeBet && enabled);
    }
    /**
     * Constructs a new `AgsBetSliderControl` object.
     * @param slider - A `FlexiSlider` object representing the slider element.
     * @param label - A `FlexiText` object representing the text label element.
     * @param menuBackgroundSprite - A `Sprite` object for the background of the bet menu.
     * @param detectionAreaSprite - A `Sprite` object for a hidden click detection area.
     */
    constructor(slider: FlexiSlider, label: FlexiText, private menuBackgroundSprite: Sprite, private detectionAreaSprite: Sprite) {
        super(slider, label);
        menuBackgroundSprite.interactive = true;

        if(detectionAreaSprite.listenerCount('pointertap') == 0) {
            detectionAreaSprite.interactive = true;

            detectionAreaSprite.on('pointertap', () => {
                uiFlags.menu(0);
            });
        }
    }

    /**
     * Gets the current credit size values from the `slotModel`.
     * @returns A `ValueList` object containing the credit size values.
     */
    protected getModelData(): ValueList<number> {
        return slotModel.read().bet.creditsPerLine;
    }

    /**
     * Determines whether or not to show the 0 value option in the slider.
     * Overrides the abstract method in `AgsAbstractSliderControl`.
     * @returns A boolean value indicating whether or not to show the 0 value option.
     */
    protected override shouldShowZeroValueOption() {
        return false;
    }

    /**
     * Saves the selected credit size value to the `slotModel`.
     * @param selectedValue - The selected credit size value.
     */
    protected saveModelData(selectedValue: number) {
        const { bet } = slotModel.read();
        bet.creditsPerLine.currentValue = selectedValue;
        bet.creditsPerLine.lastSelectedValue = selectedValue ?? 0;
        slotModel.write({ bet });
    }
}

